import { Layers } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getCompositeStatus } from "../../../../lib/groupServices";
import { getServiceIcon } from "../../../../lib/serviceIcons";
import { Service } from "../../../../types";
import { Badge } from "../../../ui/badge";
import { StatusBadge } from "../../../ui/status-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "../../../ui/table";
import { ServiceTableRow } from "./ServiceTableRow";

// ---------------------------------------------------------------------------
// DependencyRow — a read-only table row for dependency services within a stack.
// No action buttons; click still navigates to the service detail page.
// Rendered with subdued styling to distinguish it from the primary service.
// ---------------------------------------------------------------------------
function DependencyRow({ service }: { service: Service }) {
  const navigate = useNavigate();
  const ServiceIcon = getServiceIcon(service.type);

  const handleRowClick = () => {
    if (service.id && service.projectId) {
      navigate(`/projects/${service.projectId}/services/${service.id}`);
    }
  };

  return (
    <TableRow
      className="cursor-pointer bg-muted/20 hover:bg-muted/40"
      onClick={handleRowClick}
    >
      {/* Status — indented to signal hierarchy */}
      <TableCell className="pl-8">
        <StatusBadge kind="service" status={service.status} />
      </TableCell>

      {/* Name + Dependency badge */}
      <TableCell className="font-medium pl-8">
        <div className="flex items-center gap-2">
          <ServiceIcon className="h-4 w-4 text-muted-foreground/60 shrink-0" />
          <span className="text-muted-foreground">{service.name}</span>
          <Badge
            variant="outline"
            className="text-xs px-1.5 py-0 text-muted-foreground border-muted-foreground/30"
          >
            Dependency
          </Badge>
        </div>
      </TableCell>

      {/* Type */}
      <TableCell className="text-muted-foreground/70">
        {service.type || "—"}
      </TableCell>

      {/* Branch */}
      <TableCell className="text-muted-foreground/70">
        {service.repository?.branch || "—"}
      </TableCell>

      {/* Framework */}
      <TableCell className="text-muted-foreground/70">
        {service.framework || "—"}
      </TableCell>

      {/* Port */}
      <TableCell className="text-muted-foreground/70">
        {service.port ?? "—"}
      </TableCell>

      {/* Actions — empty for dependency services (read-only management) */}
      <TableCell />
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// StackGroup — renders a stack's services as a visually grouped section.
// The primary service is shown using the full ServiceTableRow (with all
// action controls). Dependency services are shown in read-only DependencyRows.
// ---------------------------------------------------------------------------
export interface StackGroupProps {
  stackName: string;
  primaryService: Service;
  dependencyServices: Service[];
  onRefresh: () => void;
  onDeploymentStarted?: () => void;
}

export function StackGroup({
  stackName,
  primaryService,
  dependencyServices,
  onRefresh,
  onDeploymentStarted,
}: StackGroupProps) {
  const allServices = [primaryService, ...dependencyServices];
  const compositeStatus = getCompositeStatus(allServices);

  return (
    <div className="rounded-md bg-card overflow-hidden">
      {/* Stack header row */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b bg-muted/40">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="text-sm font-semibold">{stackName}</span>
          <Badge
            variant="secondary"
            className="text-xs px-1.5 py-0"
          >
            Stack · {allServices.length} service{allServices.length !== 1 ? "s" : ""}
          </Badge>
        </div>
        <StatusBadge kind="service" status={compositeStatus} />
      </div>

      {/* Services table — no <TableHeader> so columns match the parent ServicesTable */}
      <Table>
        <TableBody>
          {/* Primary service — full controls */}
          <ServiceTableRow
            service={primaryService}
            onRefresh={onRefresh}
            onDeploymentStarted={onDeploymentStarted}
          />

          {/* Dependency services — read-only, subdued */}
          {dependencyServices.map((dep) => (
            <DependencyRow key={dep.id} service={dep} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
